import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Mail, Send, Users, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await axios.get(`${API}/newsletter/subscribers`);
        setSubscribers(res.data.subscribers);
        setTotal(res.data.total);
      } catch { toast.error("Failed to load subscribers"); }
      setLoading(false);
    };
    fetchSubs();
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !content.trim()) {
      toast.error("Subject and content required");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/newsletter/send`, { subject: subject.trim(), content: content.trim() });
      toast.success(res.data.message);
      setSubject("");
      setContent("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <AdminLayout title="Newsletter">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="card-surface p-6">
          <h3 className="text-white font-semibold font-['Outfit'] flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-amber-400" /> Compose Newsletter
          </h3>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <Label className="text-slate-300">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-dark mt-1" placeholder="This week in the multiverse..." required data-testid="newsletter-subject" />
            </div>
            <div>
              <Label className="text-slate-300">Content (HTML supported)</Label>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} className="input-dark mt-1 min-h-[200px]" placeholder="<p>Exciting updates from across the fandom...</p>" required data-testid="newsletter-content" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-xs">Sending to {total} subscriber{total !== 1 ? "s" : ""}</span>
              <Button type="submit" disabled={sending || total === 0} className="btn-primary gap-2" data-testid="newsletter-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending..." : "Send Newsletter"}
              </Button>
            </div>
          </form>
        </div>

        {/* Subscribers */}
        <div className="card-surface p-6">
          <h3 className="text-white font-semibold font-['Outfit'] flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-cyan-400" /> Subscribers ({total})
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-10"><div className="spinner"></div></div>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-10">
              <Mail className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No subscribers yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="subscriber-list">
              {subscribers.map((s, i) => (
                <div key={s.id || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div>
                    <span className="text-white text-sm">{s.email}</span>
                    <span className="text-slate-600 text-[10px] ml-2">{new Date(s.subscribed_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;
